import { ILayoutBaseTestGen } from '../layoutBaseTest';
import {
  PdfGen,
  PdfFont,
  PdfRect,
  PdfBrush,
  ETextAlign,
  PdfUnit,
  PdfSize,
  PdfColor,
  PdfPen,
  PageGen,
} from '../../pdf/pdf';
import '../../pdf/draw/string';
import '../../pdf/draw/primitives';
import '../../pdf/draw/shapes';
import { moduleRegistry } from '../../modules';
import { chartModule } from '../../modules/chart';
import { textModule } from '../../modules/text';
import { OptsBaseTest } from '../optsBaseTest';

const MM = PdfUnit.FromMillimeters;
const MARGIN = MM(12);

// A4 portrait: 210mm × 297mm
// Two-column layout: 90mm each, 6mm gap, 12mm side margins
// Col1: x=12mm, Col2: x=108mm (12+90+6), both right edges ≤ 198mm < 210mm

const COL1_X = MARGIN; // 12mm
const COL2_X = MM(108); // 12 + 90 + 6
const CHART_W = MM(90);
const CHART_H = MM(100);
const CHART_H_SM = MM(80);

// 2-row layout positions (h=100mm each)
const ROW1_Y = MM(20);
const ROW2_Y = MM(135); // 20 + 100 + 15

// 3-row layout positions (h=80mm each)
const R1_Y = MM(20);
const R2_Y = MM(115); // 20 + 80 + 15
const R3_Y = MM(210); // 115 + 80 + 15  → bottom 290mm < 297mm ✓

export class TestChart implements ILayoutBaseTestGen {
  async generate(pdf: PdfGen, opts: OptsBaseTest): Promise<void> {
    await pdf.addStandardFonts();

    moduleRegistry.register(chartModule);
    moduleRegistry.register(textModule);

    const font = new PdfFont(PdfFont.FONT_NAME, 14);
    const smallFont = new PdfFont(PdfFont.FONT_NAME, 10);

    const chart = moduleRegistry.get('chart');
    if (!chart) {
      throw new Error('Chart module not found');
    }

    const rectOutline = new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5);
    const titleRect = new PdfRect(MARGIN, MM(5), MM(186), MM(8));

    // Helper: draw label (5mm above chart), outline rect, then chart
    const dc = (
      page: PageGen,
      x: number,
      y: number,
      w: number,
      h: number,
      label: string,
      data: string,
      options: Record<string, string>
    ) => {
      page.drawString(
        label,
        smallFont,
        PdfBrush.Black,
        null,
        new PdfRect(x, y - MM(5), w, MM(5)),
        ETextAlign.CenterLeft
      );
      const r = new PdfRect(x, y, w, h);
      page.drawRectangle(rectOutline, null, r);
      chart.draw(chart.parse(data, options), r, ETextAlign.CenterLeft, { font, page });
    };

    // ─── Page 1: Column Charts ───────────────────────────────────────────────
    const page1 = pdf.addPage();
    page1.drawString(
      'Chart Module Tests - Column Charts',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    dc(
      page1,
      COL1_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '1. Basic Column Chart:',
      `| Item          | Value |
| ------------- | ----- |
| Mobile costs  |   100 |
| Hotel room    |  1200 |
| Bus ticket    |     2 |`,
      { type: 'column' }
    );

    dc(
      page1,
      COL2_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '2. Column Chart (ABC):',
      `| Category | Amount |
| -------- | ------ |
| A        |   50   |
| B        |   75   |
| C        |   25   |`,
      { type: 'column' }
    );

    dc(
      page1,
      COL1_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '3. Column Chart (Czech decimals):',
      `| Item      | Price  |
| --------- | ------ |
| Product A | 100,50 |
| Product B | 200,75 |
| Product C |  50,25 |`,
      { type: 'column' }
    );

    dc(
      page1,
      COL2_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '4. Column Chart (English decimals):',
      `| Item      | Price  |
| --------- | ------ |
| Product A | 100.50 |
| Product B | 200.75 |
| Product C |  50.25 |`,
      { type: 'column' }
    );

    // ─── Page 2: Bar Charts ───────────────────────────────────────────────────
    const page2 = pdf.addPage();
    page2.drawString(
      'Chart Module Tests - Bar Charts',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    dc(
      page2,
      COL1_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '5. Basic Bar Chart:',
      `| Item          | Value |
| ------------- | ----- |
| Mobile costs  |   100 |
| Hotel room    |  1200 |
| Bus ticket    |     2 |`,
      { type: 'bar' }
    );

    dc(
      page2,
      COL2_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '6. Bar Chart (Mixed decimals):',
      `| Category | Amount |
| -------- | ------ |
| Food     | 500,50 |
| Transport| 150.00 |
| Housing  | 12000  |`,
      { type: 'bar' }
    );

    dc(
      page2,
      COL1_X,
      ROW2_Y,
      MM(80),
      CHART_H_SM,
      '7. Bar Chart (80x80mm):',
      `| Label | Value |
| ----- | ----- |
| X     |    80 |
| Y     |    60 |
| Z     |    40 |`,
      { type: 'bar' }
    );

    dc(
      page2,
      COL2_X,
      ROW2_Y,
      CHART_W,
      CHART_H_SM,
      '8. Default type (column):',
      `| Item   | Value |
| ------ | ----- |
| First  |   100 |
| Second |    50 |
| Third  |    75 |`,
      {}
    );

    // ─── Page 3: Line Charts ──────────────────────────────────────────────────
    const page3 = pdf.addPage();
    page3.drawString(
      'Chart Module Tests - Line Charts',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    dc(
      page3,
      COL1_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '9. Basic Line Chart:',
      `| Month    | Sales |
| -------- | ----- |
| January  |  1000 |
| February |  1500 |
| March    |  1200 |
| April    |  1800 |`,
      { type: 'line' }
    );

    dc(
      page3,
      COL2_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '10. Line Chart (2 points):',
      `| Label | Value |
| ----- | ----- |
| Start |    50 |
| End   |   150 |`,
      { type: 'line' }
    );

    dc(
      page3,
      COL1_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '11. Line Chart (Czech decimals):',
      `| Quarter | Revenue |
| ------- | ------- |
| Q1      | 1000,50 |
| Q2      | 1500,75 |
| Q3      | 1200,25 |`,
      { type: 'line' }
    );

    dc(
      page3,
      COL2_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '12. Line Chart (many points):',
      `| X | Y  |
| - | -- |
| 1 | 10 |
| 2 | 25 |
| 3 | 15 |
| 4 | 30 |
| 5 | 45 |
| 6 | 35 |`,
      { type: 'line' }
    );

    // ─── Page 4: Area Charts ──────────────────────────────────────────────────
    const page4 = pdf.addPage();
    page4.drawString(
      'Chart Module Tests - Area Charts',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    dc(
      page4,
      COL1_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '13. Basic Area Chart:',
      `| Month    | Sales |
| -------- | ----- |
| January  |  1000 |
| February |  1500 |
| March    |  1200 |
| April    |  1800 |`,
      { type: 'area' }
    );

    dc(
      page4,
      COL2_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '14. Area Chart (Czech decimals):',
      `| Week | Value |
| ---- | ----- |
| W1   | 100,5 |
| W2   | 150,7 |
| W3   | 120,3 |
| W4   | 180,9 |`,
      { type: 'area' }
    );

    dc(
      page4,
      COL1_X,
      ROW2_Y,
      MM(80),
      CHART_H_SM,
      '15. Area Chart (80x80mm):',
      `| Point | Value |
| ----- | ----- |
| A     |    50 |
| B     |    80 |
| C     |    30 |`,
      { type: 'area' }
    );

    // Test 16: 2-point area — header row required so both points are parsed
    dc(
      page4,
      COL2_X,
      ROW2_Y,
      CHART_W,
      CHART_H_SM,
      '16. Area Chart (2 points):',
      `| Label | Value |
| ----- | ----- |
| Start |    10 |
| End   |    90 |`,
      { type: 'area' }
    );

    // ─── Page 5: Pie Charts ───────────────────────────────────────────────────
    const page5 = pdf.addPage();
    page5.drawString(
      'Chart Module Tests - Pie Charts',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    dc(
      page5,
      COL1_X,
      R1_Y,
      CHART_W,
      CHART_H_SM,
      '17. Basic Pie Chart:',
      `| Category  | Amount |
| --------- | ------ |
| Food      |    500 |
| Transport |    150 |
| Housing   |   1200 |
| Other     |     50 |`,
      { type: 'pie' }
    );

    // Test 18: header row required so both segments are parsed
    dc(
      page5,
      COL2_X,
      R1_Y,
      CHART_W,
      CHART_H_SM,
      '18. Pie Chart (2 segments):',
      `| Label  | Value |
| ------ | ----- |
| Part A |    50 |
| Part B |    50 |`,
      { type: 'pie' }
    );

    // Test 19: header row required so all 3 segments are parsed
    dc(
      page5,
      COL1_X,
      R2_Y,
      CHART_W,
      CHART_H_SM,
      '19. Pie Chart (3 segments):',
      `| Label | Value |
| ----- | ----- |
| A     |    33 |
| B     |    33 |
| C     |    34 |`,
      { type: 'pie' }
    );

    dc(
      page5,
      COL2_X,
      R2_Y,
      CHART_W,
      CHART_H_SM,
      '20. Pie Chart (Czech decimals):',
      `| Category | Value  |
| -------- | ------ |
| Item A   | 100,50 |
| Item B   | 200,50 |`,
      { type: 'pie' }
    );

    dc(
      page5,
      MM(60),
      R3_Y,
      CHART_W,
      MM(70),
      '21. Pie Chart (Dominant value):',
      `| Label | Value |
| ----- | ----- |
| Small |    10 |
| Big   |  1000 |`,
      { type: 'pie' }
    );

    // ─── Page 6: Dimension Tests ──────────────────────────────────────────────
    const page6 = pdf.addPage();
    page6.drawString(
      'Chart Module Tests - Dimensions',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    dc(
      page6,
      COL1_X,
      R1_Y,
      MM(90),
      MM(80),
      '22. Default (90x80mm):',
      `| Label | Value |
| ----- | ----- |
| A     |    10 |
| B     |    20 |`,
      {}
    );

    dc(
      page6,
      COL2_X,
      R1_Y,
      MM(80),
      MM(50),
      '23. Custom (80x50mm):',
      `| Label | Value |
| ----- | ----- |
| A     |    10 |
| B     |    20 |`,
      { width: '80', height: '50' }
    );

    // Full usable width: 210 - 2*12 = 186mm
    dc(
      page6,
      COL1_X,
      R2_Y,
      MM(186),
      MM(100),
      '24. Full width (186x100mm):',
      `| Label | Value |
| ----- | ----- |
| A     |    10 |
| B     |    20 |
| C     |    15 |`,
      { width: '186', height: '100' }
    );

    dc(
      page6,
      COL2_X,
      R3_Y,
      MM(50),
      MM(50),
      '25. Small (50x50mm):',
      `| Label | Value |
| ----- | ----- |
| A     |    10 |
| B     |    20 |`,
      { width: '50', height: '50' }
    );

    // ─── Page 7: Edge Cases ───────────────────────────────────────────────────
    const page7 = pdf.addPage();
    page7.drawString(
      'Chart Module Tests - Edge Cases',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    dc(page7, COL1_X, R1_Y, CHART_W, CHART_H_SM, '26. Empty data:', ``, { type: 'column' });

    dc(
      page7,
      COL2_X,
      R1_Y,
      CHART_W,
      CHART_H_SM,
      '27. Single data point:',
      `| Label | Value |
| ----- | ----- |
| Only  |    50 |`,
      { type: 'column' }
    );

    dc(
      page7,
      COL1_X,
      R2_Y,
      CHART_W,
      CHART_H_SM,
      '28. All same values:',
      `| Label | Value |
| ----- | ----- |
| A     |    50 |
| B     |    50 |
| C     |    50 |`,
      { type: 'bar' }
    );

    dc(
      page7,
      COL2_X,
      R2_Y,
      CHART_W,
      CHART_H_SM,
      '29. With zero values:',
      `| Label | Value |
| ----- | ----- |
| A     |     0 |
| B     |    50 |
| C     |    25 |`,
      { type: 'column' }
    );

    // Test 30: CSV fallback — first line is header (skipped), remaining lines are data
    dc(
      page7,
      COL1_X,
      R3_Y,
      CHART_W,
      CHART_H_SM,
      '30. CSV fallback format:',
      `Label, Value
Item 1, 100
Item 2, 200
Item 3, 150`,
      { type: 'bar' }
    );

    // ─── Page 8: All Types Comparison ────────────────────────────────────────
    const page8 = pdf.addPage();
    page8.drawString(
      'Chart Module Tests - All Types Comparison',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    const compData = `| Item  | Value |
| ----- | ----- |
| A     |    80 |
| B     |    60 |
| C     |    45 |
| D     |    30 |`;

    // 3 per row: each 58mm wide, 6mm gap → 3×58 + 2×6 = 186mm = usable width ✓
    const CW = MM(58);
    const CX1 = MARGIN;
    const CX2 = MM(76); // 12 + 58 + 6
    const CX3 = MM(140); // 76 + 58 + 6
    const CH = MM(70);

    dc(page8, CX1, R1_Y, CW, CH, 'Column:', compData, { type: 'column' });
    dc(page8, CX2, R1_Y, CW, CH, 'Bar:', compData, { type: 'bar' });
    dc(page8, CX3, R1_Y, CW, CH, 'Line:', compData, { type: 'line' });

    // Row 2: area + pie (pie needs square-ish rect, use 90mm wide centered)
    dc(page8, CX1, R2_Y, CW, CHART_H_SM, 'Area:', compData, { type: 'area' });
    dc(page8, CX2, R2_Y, CHART_W, CHART_H_SM, 'Pie:', compData, { type: 'pie' });

    // ─── Page 9: Real-world Examples ─────────────────────────────────────────
    const page9 = pdf.addPage();
    page9.drawString(
      'Chart Module Tests - Real-world Examples',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    dc(
      page9,
      COL1_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      'Monthly Expenses (Column):',
      `| Category      | Amount |
| ------------- | ------ |
| Rent          |  12000 |
| Food          |   5000 |
| Transport     |   1500 |
| Utilities     |   2500 |
| Entertainment |   1000 |`,
      { type: 'column' }
    );

    dc(
      page9,
      COL2_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      'Quarterly Sales (Line):',
      `| Quarter | Sales    |
| ------- | -------- |
| Q1      | 15000,50 |
| Q2      | 18500,00 |
| Q3      | 16200,75 |
| Q4      | 21000,25 |`,
      { type: 'line' }
    );

    dc(
      page9,
      COL1_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      'Market Share (Pie):',
      `| Company  | Share |
| -------- | ----- |
| CompanyA |    45 |
| CompanyB |    30 |
| CompanyC |    15 |
| Others   |    10 |`,
      { type: 'pie' }
    );

    dc(
      page9,
      COL2_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      'Temperature Trend (Area):',
      `| Day | Temp |
| --- | ---- |
| Mon | 15.5 |
| Tue | 17.2 |
| Wed | 14.8 |
| Thu | 16.0 |
| Fri | 18.5 |
| Sat | 20.1 |
| Sun | 19.3 |`,
      { type: 'area' }
    );

    // ─── Page 11: Donut + Lollipop + Step + Histogram ────────────────────────
    const page11 = pdf.addPage();
    page11.drawString(
      'New Charts — Donut · Lollipop · Step · Histogram',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    dc(
      page11,
      COL1_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '31. Donut Chart:',
      `| Category  | Value |
| --------- | ----- |
| Food      |   500 |
| Transport |   150 |
| Housing   |  1200 |
| Other     |    50 |`,
      { type: 'donut' }
    );

    dc(
      page11,
      COL2_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '32. Lollipop Chart:',
      `| Item     | Value |
| -------- | ----- |
| Jan      |    80 |
| Feb      |   120 |
| Mar      |    60 |
| Apr      |   150 |
| May      |    90 |`,
      { type: 'lollipop' }
    );

    dc(
      page11,
      COL1_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '33. Step Chart:',
      `| Month | Value |
| ----- | ----- |
| Jan   |    10 |
| Feb   |    25 |
| Mar   |    20 |
| Apr   |    40 |
| May   |    35 |`,
      { type: 'step' }
    );

    dc(
      page11,
      COL2_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '34. Histogram:',
      `| Bin    | Count |
| ------ | ----- |
| 0–10   |     5 |
| 10–20  |    12 |
| 20–30  |    18 |
| 30–40  |    14 |
| 40–50  |     8 |
| 50–60  |     3 |`,
      { type: 'histogram' }
    );

    // ─── Page 12: Funnel + Pyramid ────────────────────────────────────────────
    const page12 = pdf.addPage();
    page12.drawString(
      'New Charts — Funnel · Pyramid',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    dc(
      page12,
      COL1_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '35. Funnel Chart:',
      `| Stage      | Count |
| ---------- | ----- |
| Visitors   |  5000 |
| Leads      |  2000 |
| Prospects  |   800 |
| Customers  |   300 |
| Loyal      |   100 |`,
      { type: 'funnel' }
    );

    dc(
      page12,
      COL2_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '36. Funnel Chart (4 stages):',
      `| Stage      | Value |
| ---------- | ----- |
| Awareness  |  1000 |
| Interest   |   600 |
| Desire     |   300 |
| Action     |   100 |`,
      { type: 'funnel' }
    );

    dc(
      page12,
      COL1_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '37. Pyramid Chart (Maslow):',
      `| Level            | Value |
| ---------------- | ----- |
| Physiological    |   100 |
| Safety           |    80 |
| Social           |    60 |
| Esteem           |    40 |
| Self-actual.     |    20 |`,
      { type: 'pyramid' }
    );

    dc(
      page12,
      COL2_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '38. Pyramid Chart (3 layers):',
      `| Level    | Value |
| -------- | ----- |
| Base     |    90 |
| Middle   |    60 |
| Top      |    30 |`,
      { type: 'pyramid' }
    );

    // ─── Page 13: Stacked Column + Stacked Bar ────────────────────────────────
    const page13 = pdf.addPage();
    page13.drawString(
      'New Charts — Stacked Column · Stacked Bar',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    dc(
      page13,
      COL1_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '39. Stacked Column Chart:',
      `| Quarter | Online | Store | Phone |
| ------- | ------ | ----- | ----- |
| Q1      |     30 |    20 |    10 |
| Q2      |     40 |    25 |    15 |
| Q3      |     35 |    22 |    12 |
| Q4      |     50 |    30 |    18 |`,
      { type: 'stacked-column' }
    );

    dc(
      page13,
      COL2_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '40. Stacked Column (2 series):',
      `| Month | Budget | Actual |
| ----- | ------ | ------ |
| Jan   |    100 |     90 |
| Feb   |    120 |    130 |
| Mar   |    110 |    105 |`,
      { type: 'stacked-column' }
    );

    dc(
      page13,
      COL1_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '41. Stacked Bar Chart:',
      `| Category     | Food | Transport | Other |
| ------------ | ---- | --------- | ----- |
| Household A  |  500 |       150 |   200 |
| Household B  |  700 |       300 |   100 |
| Household C  |  400 |       200 |   350 |`,
      { type: 'stacked-bar' }
    );

    dc(
      page13,
      COL2_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '42. Stacked Bar (long labels):',
      `| Department           | Junior | Senior |
| -------------------- | ------ | ------ |
| Engineering          |      8 |     12 |
| Product              |      4 |      6 |
| Marketing            |      5 |      4 |`,
      { type: 'stacked-bar' }
    );

    // ─── Page 14: Dumbbell + Slope ────────────────────────────────────────────
    const page14 = pdf.addPage();
    page14.drawString(
      'New Charts — Dumbbell · Slope',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    dc(
      page14,
      COL1_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '43. Dumbbell Chart:',
      `| Subject | Before | After |
| ------- | ------ | ----- |
| Math    |     60 |    80 |
| English |     72 |    85 |
| Science |     55 |    75 |
| History |     68 |    70 |
| Art     |     80 |    88 |`,
      { type: 'dumbbell' }
    );

    dc(
      page14,
      COL2_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '44. Dumbbell (scores):',
      `| Country   | 2010 | 2023 |
| --------- | ---- | ---- |
| Country A |   45 |   62 |
| Country B |   38 |   55 |
| Country C |   52 |   48 |
| Country D |   60 |   71 |`,
      { type: 'dumbbell' }
    );

    dc(
      page14,
      COL1_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '45. Slope Chart:',
      `| Product   | 2020 | 2023 |
| --------- | ---- | ---- |
| Product A |   30 |   55 |
| Product B |   45 |   38 |
| Product C |   20 |   42 |
| Product D |   60 |   65 |`,
      { type: 'slope' }
    );

    dc(
      page14,
      COL2_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '46. Slope (grades):',
      `| Student | Term 1 | Term 2 |
| ------- | ------ | ------ |
| Alice   |     72 |     88 |
| Bob     |     85 |     79 |
| Carol   |     68 |     92 |`,
      { type: 'slope' }
    );

    // ─── Page 15: Progress Bars ───────────────────────────────────────────────
    const page15 = pdf.addPage();
    page15.drawString(
      'New Charts — Progress Bars',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    dc(
      page15,
      COL1_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '47. Progress Bars (with max):',
      `| Subject     | Score | Max |
| ----------- | ----- | --- |
| Mathematics |    85 | 100 |
| English     |    92 | 100 |
| Science     |    78 | 100 |
| History     |    65 | 100 |
| Art         |    95 | 100 |`,
      { type: 'progress' }
    );

    dc(
      page15,
      COL2_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '48. Progress Bars (custom max):',
      `| Task          | Done | Total |
| ------------- | ---- | ----- |
| Chapter 1     |   15 |    20 |
| Chapter 2     |    8 |    15 |
| Chapter 3     |   12 |    12 |
| Exercises     |   45 |    60 |`,
      { type: 'progress' }
    );

    dc(
      page15,
      COL1_X,
      ROW2_Y,
      CHART_W,
      MM(50),
      '49. Progress (2 bars):',
      `| Label   | Value | Max |
| ------- | ----- | --- |
| Done    |    75 | 100 |
| Pending |    25 | 100 |`,
      { type: 'progress' }
    );

    dc(
      page15,
      COL2_X,
      ROW2_Y,
      CHART_W,
      MM(50),
      '50. Progress (percentages ~100):',
      `| Metric       | Value | Max |
| ------------ | ----- | --- |
| Completion   |    95 | 100 |
| Accuracy     |    73 | 100 |
| Speed        |    61 | 100 |`,
      { type: 'progress' }
    );

    // ─── Page 10: Palette Tests ───────────────────────────────────────────────
    const page10 = pdf.addPage();
    page10.drawString(
      'Chart Module Tests - Palettes',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    const paletteData = `| Item | Value |
| ---- | ----- |
| A    |    80 |
| B    |    60 |
| C    |    45 |
| D    |    30 |
| E    |    20 |`;

    const palettes = [
      'soft',
      'deep',
      'professional',
      'pastelStrong',
      'colorbrewer',
      'tableau',
      'd3default',
      'material',
    ];
    // 4 columns: 42mm wide, 4mm gap → 4×42 + 3×4 = 180mm < 186mm ✓
    // x positions: 12, 58 (12+42+4), 104, 150
    const PW = MM(42);
    const PH = MM(50);
    const pxs = [MARGIN, MM(58), MM(104), MM(150)];
    // 4 rows showing different chart types, row height = PH + 10mm gap
    const chartTypes: ('column' | 'bar' | 'pie' | 'line')[] = ['column', 'bar', 'pie', 'line'];

    for (let row = 0; row < 4; row++) {
      const py = MM(20) + row * (PH + MM(10));
      for (let col = 0; col < 4; col++) {
        dc(page10, pxs[col], py, PW, PH, `${palettes[col + row * 4]}:`, paletteData, {
          type: chartTypes[row],
          palette: palettes[col + row * 4],
        });
      }
    }

    // ─── Page 16: Radar & Waffle ──────────────────────────────────────────────
    const page16 = pdf.addPage();
    page16.drawString(
      'Chart Module Tests - Radar & Waffle',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    dc(
      page16,
      COL1_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '51. Radar (skills):',
      `| Axis    | Value |
| ------- | ----- |
| Reading |    85 |
| Writing |    70 |
| Math    |    90 |
| Science |    60 |
| History |    75 |`,
      { type: 'radar' }
    );

    dc(
      page16,
      COL2_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '52. Radar (6 axes, no values):',
      `| Axis     | Value |
| -------- | ----- |
| Speed    |    80 |
| Power    |    55 |
| Stamina  |    65 |
| Agility  |    90 |
| Defense  |    40 |
| Skill    |    70 |`,
      { type: 'radar', values: 'false' }
    );

    dc(
      page16,
      COL1_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '53. Waffle (3 categories):',
      `| Category   | Percent |
| ---------- | ------- |
| Completed  |      62 |
| In Progress|      23 |
| Not started|      15 |`,
      { type: 'waffle' }
    );

    dc(
      page16,
      COL2_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '54. Waffle (5 categories):',
      `| Category | Percent |
| -------- | ------- |
| A        |      30 |
| B        |      25 |
| C        |      20 |
| D        |      15 |
| E        |      10 |`,
      { type: 'waffle' }
    );

    // ─── Page 17: Waterfall & Concentric ──────────────────────────────────────
    const page17 = pdf.addPage();
    page17.drawString(
      'Chart Module Tests - Waterfall & Concentric',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    dc(
      page17,
      COL1_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '55. Waterfall (budget):',
      `| Category    | Value |
| ----------- | ----- |
| Revenue     |   500 |
| COGS        |  -200 |
| Gross Profit|     0 |
| OpEx        |  -150 |
| EBIT        |     0 |
| Tax         |   -30 |
| Net Profit  |     0 |`,
      { type: 'waterfall' }
    );

    dc(
      page17,
      COL2_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '56. Waterfall (cashflow):',
      `| Month   | Value |
| ------- | ----- |
| Jan     |   120 |
| Feb     |   -40 |
| Mar     |    80 |
| Apr     |   -20 |
| May     |    60 |
| Total   |     0 |`,
      { type: 'waterfall' }
    );

    dc(
      page17,
      COL1_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '57. Concentric (3 rings, values):',
      `| Label           | Value |
| --------------- | ----- |
| Safety          |    40 |
| Social          |    35 |
| Self-actualizat |    25 |`,
      { type: 'concentric' }
    );

    dc(
      page17,
      COL2_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '58. Concentric (5 rings, no values):',
      `| Label     | Value |
| --------- | ----- |
| Critical  |    10 |
| Important |    20 |
| Moderate  |    30 |
| Low       |    25 |
| Minimal   |    15 |`,
      { type: 'concentric', values: 'false' }
    );

    // ─── Page 18: Timeline & Venn ─────────────────────────────────────────────
    const page18 = pdf.addPage();
    page18.drawString(
      'Chart Module Tests - Timeline & Venn',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    dc(
      page18,
      COL1_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '59. Timeline (history):',
      `| Event              | Year |
| ------------------ | ---- |
| Declaration        | 1776 |
| Constitution       | 1789 |
| Civil War          | 1861 |
| First Flight       | 1903 |
| Moon Landing       | 1969 |`,
      { type: 'timeline' }
    );

    dc(
      page18,
      COL2_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '60. Timeline (dates, no values):',
      `| Event       | Date     |
| ----------- | -------- |
| Kickoff     | Jan 2024 |
| Design      | Mar 2024 |
| Development | Jun 2024 |
| Testing     | Oct 2024 |
| Launch      | Jan 2025 |`,
      { type: 'timeline', values: 'false' }
    );

    dc(
      page18,
      COL1_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '61. Venn (2 sets):',
      `| Set | Value |
| --- | ----- |
| A   |    30 |
| B   |    25 |
| AB  |    15 |`,
      { type: 'venn' }
    );

    dc(
      page18,
      COL2_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '62. Venn (3 sets):',
      `| Set | Value |
| --- | ----- |
| A   |    20 |
| B   |    18 |
| C   |    15 |
| AB  |     8 |
| AC  |     6 |
| BC  |     5 |
| ABC |     3 |`,
      { type: 'venn' }
    );

    // ─── Page 19: Scatter & Bubble ────────────────────────────────────────────
    const page19 = pdf.addPage();
    page19.drawString(
      'Chart Module Tests - Scatter & Bubble',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    dc(
      page19,
      COL1_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '63. Scatter (correlation):',
      `| Category | X  | Y  |
| -------- | -- | -- |
| A        |  1 | 10 |
| B        |  2 | 15 |
| C        |  3 | 12 |
| D        |  4 | 20 |
| E        |  5 | 18 |
| F        |  6 | 25 |`,
      { type: 'scatter' }
    );

    dc(
      page19,
      COL2_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '64. Scatter (spread):',
      `| Label | X  | Y  |
| ----- | -- | -- |
| P1    | 10 | 40 |
| P2    | 20 | 60 |
| P3    | 30 | 20 |
| P4    | 40 | 80 |
| P5    | 50 | 55 |
| P6    | 60 | 35 |
| P7    | 70 | 70 |`,
      { type: 'scatter' }
    );

    dc(
      page19,
      COL1_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '65. Bubble (3 vars):',
      `| Label | X  | Y  | Size |
| ----- | -- | -- | ---- |
| Alpha |  1 | 10 |   30 |
| Beta  |  3 | 25 |   60 |
| Gamma |  5 | 15 |   45 |
| Delta |  7 | 35 |   80 |
| Eps   |  9 | 20 |   20 |`,
      { type: 'bubble' }
    );

    dc(
      page19,
      COL2_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '66. Bubble (economies):',
      `| Country | GDP | HDI | Pop |
| ------- | --- | --- | --- |
| A       |  50 |  75 |  80 |
| B       |  30 |  60 |  40 |
| C       |  80 |  85 |  60 |
| D       |  20 |  45 |  90 |
| E       |  65 |  70 |  30 |`,
      { type: 'bubble' }
    );

    // ─── Page 20: Heatmap & Box Plot ──────────────────────────────────────────
    const page20 = pdf.addPage();
    page20.drawString(
      'Chart Module Tests - Heatmap & Box Plot',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    dc(
      page20,
      COL1_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '67. Heatmap (grades):',
      `|         | Math | English | Science |
| ------- | ---- | ------- | ------- |
| Alice   |   90 |      75 |      85 |
| Bob     |   60 |      80 |      70 |
| Carol   |   75 |      90 |      65 |
| Dave    |   85 |      65 |      95 |`,
      { type: 'heatmap' }
    );

    dc(
      page20,
      COL2_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '68. Heatmap (no values):',
      `|     | Mon | Tue | Wed | Thu | Fri |
| --- | --- | --- | --- | --- | --- |
| AM  |  80 |  60 |  90 |  40 |  70 |
| PM  |  50 |  75 |  30 |  85 |  65 |`,
      { type: 'heatmap', values: 'false' }
    );

    dc(
      page20,
      COL1_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '69. Box Plot (test scores):',
      `| Class | Min | Q1 | Median | Q3 | Max |
| ----- | --- | -- | ------ | -- | --- |
| A     |  45 | 62 |     72 | 83 |  98 |
| B     |  30 | 55 |     68 | 79 |  95 |
| C     |  60 | 70 |     78 | 88 | 100 |`,
      { type: 'box-plot' }
    );

    dc(
      page20,
      COL2_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '70. Box Plot (no values):',
      `| Group    | Min | Q1  | Median | Q3  | Max |
| -------- | --- | --- | ------ | --- | --- |
| Adults   | 150 | 163 |    170 | 178 | 200 |
| Teens    | 140 | 155 |    162 | 170 | 185 |
| Children | 100 | 115 |    125 | 135 | 155 |`,
      { type: 'box-plot', values: 'false' }
    );

    // ─── Page 21: Population Pyramid ─────────────────────────────────────────
    const page21 = pdf.addPage();
    page21.drawString(
      'Chart Module Tests - Population Pyramid',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    dc(
      page21,
      COL1_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '71. Population Pyramid (age/gender):',
      `| AgeGroup | Male | Female |
| -------- | ---- | ------ |
| 0-14     | 1200 |   1100 |
| 15-29    | 1400 |   1350 |
| 30-44    | 1300 |   1280 |
| 45-59    | 1100 |   1150 |
| 60-74    |  800 |    900 |
| 75+      |  400 |    550 |`,
      { type: 'population-pyramid' }
    );

    dc(
      page21,
      COL2_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '72. Population Pyramid (no values):',
      `| Group | Left | Right |
| ----- | ---- | ----- |
| A     |   30 |    45 |
| B     |   50 |    60 |
| C     |   40 |    35 |
| D     |   25 |    30 |`,
      { type: 'population-pyramid', values: 'false' }
    );

    // ─── Page 22: Treemap & Rose ──────────────────────────────────────────────
    const page22 = pdf.addPage();
    page22.drawString(
      'Chart Module Tests - Treemap & Rose',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    dc(
      page22,
      COL1_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '73. Treemap (market share):',
      `| Category | Value |
| -------- | ----- |
| Finance  |    35 |
| Tech     |    28 |
| Health   |    18 |
| Energy   |    12 |
| Other    |     7 |`,
      { type: 'treemap' }
    );

    dc(
      page22,
      COL2_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '74. Treemap (no values):',
      `| Category | Value |
| -------- | ----- |
| Finance  |    35 |
| Tech     |    28 |
| Health   |    18 |
| Energy   |    12 |
| Other    |     7 |`,
      { type: 'treemap', values: 'false' }
    );

    dc(
      page22,
      COL1_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '75. Rose (wind directions):',
      `| Direction | Value |
| --------- | ----- |
| N  |  80 |
| NE |  45 |
| E  |  60 |
| SE |  35 |
| S  |  70 |
| SW |  55 |
| W  |  40 |
| NW |  50 |`,
      { type: 'rose' }
    );

    dc(
      page22,
      COL2_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '76. Rose (no values):',
      `| Direction | Value |
| --------- | ----- |
| N  |  80 |
| NE |  45 |
| E  |  60 |
| SE |  35 |
| S  |  70 |
| SW |  55 |
| W  |  40 |
| NW |  50 |`,
      { type: 'rose', values: 'false' }
    );

    // ─── Page 23: Strip & Icon Array ──────────────────────────────────────────
    const page23 = pdf.addPage();
    page23.drawString(
      'Chart Module Tests - Strip & Icon Array',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    dc(
      page23,
      COL1_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '77. Strip plot (scores by class):',
      `| Class A | 72 |
| Class A | 85 |
| Class A | 68 |
| Class A | 91 |
| Class A | 77 |
| Class B | 65 |
| Class B | 78 |
| Class B | 88 |
| Class B | 59 |
| Class C | 82 |
| Class C | 74 |
| Class C | 95 |`,
      { type: 'strip' }
    );

    dc(
      page23,
      COL2_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '78. Strip (no values):',
      `| Class A | 72 |
| Class A | 85 |
| Class A | 68 |
| Class A | 91 |
| Class A | 77 |
| Class B | 65 |
| Class B | 78 |
| Class B | 88 |
| Class B | 59 |
| Class C | 82 |
| Class C | 74 |
| Class C | 95 |`,
      { type: 'strip', values: 'false' }
    );

    dc(
      page23,
      COL1_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '79. Icon Array (3 categories):',
      `| Category    | Percent |
| ----------- | ------- |
| Completed   |      62 |
| In Progress |      23 |
| Not started |      15 |`,
      { type: 'icon-array' }
    );

    dc(
      page23,
      COL2_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '80. Icon Array (4 categories, no values):',
      `| Category | Percent |
| -------- | ------- |
| A        |      40 |
| B        |      30 |
| C        |      20 |
| D        |      10 |`,
      { type: 'icon-array', values: 'false' }
    );

    // ─── Page 24: Bullet & Parallel Coords ────────────────────────────────────
    const page24 = pdf.addPage();
    page24.drawString(
      'Chart Module Tests - Bullet & Parallel Coords',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    dc(
      page24,
      COL1_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '81. Bullet Chart (KPIs):',
      `| Label  | Value | Target | Poor | OK  | Good |
| ------ | ----- | ------ | ---- | --- | ---- |
| Sales  |    87 |     90 |   60 |  80 |  100 |
| Cost   |    42 |     40 |   50 |  35 |   25 |
| Rating |     4 |      5 |    2 |   3 |    5 |`,
      { type: 'bullet' }
    );

    dc(
      page24,
      COL2_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '82. Bullet (no values):',
      `| Label  | Value | Target | Poor | OK  | Good |
| ------ | ----- | ------ | ---- | --- | ---- |
| Sales  |    87 |     90 |   60 |  80 |  100 |
| Cost   |    42 |     40 |   50 |  35 |   25 |
| Rating |     4 |      5 |    2 |   3 |    5 |`,
      { type: 'bullet', values: 'false' }
    );

    dc(
      page24,
      COL1_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '83. Parallel Coords (students):',
      `| Student | Math | English | Science | History |
| ------- | ---- | ------- | ------- | ------- |
| Alice   |   85 |      72 |      90 |      68 |
| Bob     |   60 |      88 |      65 |      78 |
| Carol   |   75 |      65 |      80 |      85 |
| Dave    |   90 |      78 |      70 |      72 |`,
      { type: 'parallel-coords' }
    );

    dc(
      page24,
      COL2_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '84. Parallel Coords (no values):',
      `| Student | Math | English | Science | History |
| ------- | ---- | ------- | ------- | ------- |
| Alice   |   85 |      72 |      90 |      68 |
| Bob     |   60 |      88 |      65 |      78 |
| Carol   |   75 |      65 |      80 |      85 |
| Dave    |   90 |      78 |      70 |      72 |`,
      { type: 'parallel-coords', values: 'false' }
    );

    // ─── Page 25: Gantt ────────────────────────────────────────────────────────
    const page25 = pdf.addPage();
    page25.drawString(
      'Chart Module Tests - Gantt',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    dc(
      page25,
      COL1_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '85. Gantt (project):',
      `| Task        | Start | End |
| ----------- | ----- | --- |
| Planning    |     1 |   3 |
| Design      |     2 |   5 |
| Development |     4 |  10 |
| Testing     |     9 |  12 |
| Deployment  |    11 |  13 |`,
      { type: 'gantt' }
    );

    dc(
      page25,
      COL2_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '86. Gantt (no values):',
      `| Task        | Start | End |
| ----------- | ----- | --- |
| Planning    |     1 |   3 |
| Design      |     2 |   5 |
| Development |     4 |  10 |
| Testing     |     9 |  12 |
| Deployment  |    11 |  13 |`,
      { type: 'gantt', values: 'false' }
    );

    dc(
      page25,
      COL1_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '87. Gantt (months):',
      `| Task      | Start | End |
| --------- | ----- | --- |
| Research  |     1 |   4 |
| Prototype |     3 |   7 |
| Pilot     |     6 |   9 |
| Rollout   |     8 |  12 |`,
      { type: 'gantt' }
    );

    dc(
      page25,
      COL2_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '88. Gantt (4 tasks, no values):',
      `| Task      | Start | End |
| --------- | ----- | --- |
| Research  |     1 |   4 |
| Prototype |     3 |   7 |
| Pilot     |     6 |   9 |
| Rollout   |     8 |  12 |`,
      { type: 'gantt', values: 'false' }
    );

    // ─── Page 26: Dot Map & Network ────────────────────────────────────────────
    const page26 = pdf.addPage();
    page26.drawString(
      'Chart Module Tests - Dot Map & Network',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    dc(
      page26,
      COL1_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '89. Dot Map:',
      `| Label  | X  | Y  | Size |
| ------ | -- | -- | ---- |
| London | 45 | 35 |   80 |
| Paris  | 47 | 42 |   60 |
| Berlin | 54 | 32 |   50 |
| Madrid | 38 | 55 |   45 |
| Rome   | 52 | 53 |   40 |`,
      { type: 'dot-map' }
    );

    dc(
      page26,
      COL2_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '90. Dot Map (no values):',
      `| Label  | X  | Y  | Size |
| ------ | -- | -- | ---- |
| London | 45 | 35 |   80 |
| Paris  | 47 | 42 |   60 |
| Berlin | 54 | 32 |   50 |
| Madrid | 38 | 55 |   45 |
| Rome   | 52 | 53 |   40 |`,
      { type: 'dot-map', values: 'false' }
    );

    dc(
      page26,
      COL1_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '91. Network (5 nodes):',
      `| From | To |
| ---- | -- |
| A    | B  |
| A    | C  |
| B    | D  |
| C    | D  |
| D    | E  |
| A    | E  |`,
      { type: 'network' }
    );

    dc(
      page26,
      COL2_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '92. Network (no labels):',
      `| From    | To      |
| ------- | ------- |
| Alpha   | Beta    |
| Alpha   | Gamma   |
| Beta    | Delta   |
| Gamma   | Delta   |
| Delta   | Epsilon |`,
      { type: 'network', values: 'false' }
    );

    // ─── Page 27: Marimekko & Streamgraph ─────────────────────────────────────
    const page27 = pdf.addPage();
    page27.drawString(
      'Chart Module Tests - Marimekko & Streamgraph',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    dc(
      page27,
      COL1_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '93. Marimekko (market share):',
      `| Region | Weight | Budget | Standard | Premium |
| ------ | ------ | ------ | -------- | ------- |
| North  |     40 |     25 |       45 |      30 |
| South  |     30 |     50 |       30 |      20 |
| East   |     20 |     35 |       40 |      25 |
| West   |     10 |     60 |       20 |      20 |`,
      { type: 'marimekko' }
    );

    dc(
      page27,
      COL2_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '94. Marimekko (no values):',
      `| Region | Weight | Budget | Standard | Premium |
| ------ | ------ | ------ | -------- | ------- |
| North  |     40 |     25 |       45 |      30 |
| South  |     30 |     50 |       30 |      20 |
| East   |     20 |     35 |       40 |      25 |
| West   |     10 |     60 |       20 |      20 |`,
      { type: 'marimekko', values: 'false' }
    );

    dc(
      page27,
      COL1_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '95. Streamgraph (activities):',
      `| Month | Music | Video | Reading | Gaming |
| ----- | ----- | ----- | ------- | ------ |
| Jan   |    15 |    20 |      10 |      8 |
| Feb   |    18 |    15 |      12 |     10 |
| Mar   |    22 |    18 |      11 |     12 |
| Apr   |    20 |    25 |       9 |     15 |
| May   |    25 |    22 |      10 |     20 |
| Jun   |    30 |    20 |       8 |     25 |`,
      { type: 'streamgraph' }
    );

    dc(
      page27,
      COL2_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '96. Streamgraph (no legend):',
      `| Quarter | Tech | Health | Finance | Energy |
| ------- | ---- | ------ | ------- | ------ |
| Q1      |   30 |     20 |      25 |     15 |
| Q2      |   35 |     22 |      20 |     18 |
| Q3      |   28 |     30 |      22 |     20 |
| Q4      |   40 |     25 |      30 |     22 |`,
      { type: 'streamgraph', values: 'false' }
    );

    // ─── Page 28: Sunburst & Arc Diagram ──────────────────────────────────────
    const page28 = pdf.addPage();
    page28.drawString(
      'Chart Module Tests - Sunburst & Arc Diagram',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    dc(
      page28,
      COL1_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '97. Sunburst (subjects):',
      `| Group   | Subject  | Value |
| ------- | -------- | ----- |
| Science | Biology  |    20 |
| Science | Physics  |    15 |
| Science | Chemistry|    12 |
| Arts    | Music    |    18 |
| Arts    | Drama    |    14 |
| Math    | Algebra  |    22 |
| Math    | Geometry |    16 |`,
      { type: 'sunburst' }
    );

    dc(
      page28,
      COL2_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '98. Sunburst (no child labels):',
      `| Group   | Subject  | Value |
| ------- | -------- | ----- |
| Science | Biology  |    20 |
| Science | Physics  |    15 |
| Science | Chemistry|    12 |
| Arts    | Music    |    18 |
| Arts    | Drama    |    14 |
| Math    | Algebra  |    22 |
| Math    | Geometry |    16 |`,
      { type: 'sunburst', values: 'false' }
    );

    dc(
      page28,
      COL1_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '99. Arc Diagram (social):',
      `| From  | To    | Weight |
| ----- | ----- | ------ |
| Alice | Bob   |      3 |
| Alice | Carol |      2 |
| Bob   | Dave  |      4 |
| Carol | Dave  |      1 |
| Dave  | Eve   |      3 |
| Bob   | Eve   |      2 |`,
      { type: 'arc-diagram' }
    );

    dc(
      page28,
      COL2_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '100. Arc Diagram (no labels):',
      `| From  | To    | Weight |
| ----- | ----- | ------ |
| Alice | Bob   |      3 |
| Alice | Carol |      2 |
| Bob   | Dave  |      4 |
| Carol | Dave  |      1 |
| Dave  | Eve   |      3 |
| Bob   | Eve   |      2 |`,
      { type: 'arc-diagram', values: 'false' }
    );

    // ─── Page 29: Sankey ───────────────────────────────────────────────────────
    const page29 = pdf.addPage();
    page29.drawString(
      'Chart Module Tests - Sankey',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    dc(
      page29,
      COL1_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '101. Sankey (energy flow):',
      `| From        | To          | Value |
| ----------- | ----------- | ----- |
| Solar       | Electricity |    45 |
| Wind        | Electricity |    35 |
| Coal        | Electricity |    60 |
| Electricity | Industry    |    70 |
| Electricity | Homes       |    50 |
| Electricity | Transport   |    20 |`,
      { type: 'sankey' }
    );

    dc(
      page29,
      COL2_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '102. Sankey (no values):',
      `| From        | To          | Value |
| ----------- | ----------- | ----- |
| Solar       | Electricity |    45 |
| Wind        | Electricity |    35 |
| Coal        | Electricity |    60 |
| Electricity | Industry    |    70 |
| Electricity | Homes       |    50 |
| Electricity | Transport   |    20 |`,
      { type: 'sankey', values: 'false' }
    );

    dc(
      page29,
      COL1_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '103. Sankey (budget flow):',
      `| From       | To         | Value |
| ---------- | ---------- | ----- |
| Government |  Education |    30 |
| Government | Healthcare |    40 |
| Government | Defence    |    20 |
| Government | Transport  |    10 |`,
      { type: 'sankey' }
    );

    dc(
      page29,
      COL2_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '104. Sankey (budget, no values):',
      `| From       | To         | Value |
| ---------- | ---------- | ----- |
| Government |  Education |    30 |
| Government | Healthcare |    40 |
| Government | Defence    |    20 |
| Government | Transport  |    10 |`,
      { type: 'sankey', values: 'false' }
    );

    // ─── Page 30: Calendar Heatmap & Chord ────────────────────────────────────
    const page30 = pdf.addPage();
    page30.drawString(
      'Chart Module Tests - Calendar Heatmap & Chord',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    dc(
      page30,
      COL1_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '105. Calendar Heatmap:',
      `| Date       | Value |
| ---------- | ----- |
| 2024-01-08 |     3 |
| 2024-01-09 |     5 |
| 2024-01-10 |     8 |
| 2024-01-11 |     4 |
| 2024-01-13 |     6 |
| 2024-01-15 |     7 |
| 2024-01-16 |     3 |
| 2024-01-18 |     9 |
| 2024-01-19 |     2 |
| 2024-01-22 |     8 |
| 2024-01-24 |     5 |
| 2024-01-25 |     7 |
| 2024-01-26 |     4 |
| 2024-01-29 |     6 |
| 2024-01-30 |    10 |
| 2024-02-01 |     4 |
| 2024-02-02 |     8 |
| 2024-02-05 |     5 |
| 2024-02-06 |     7 |
| 2024-02-07 |     9 |
| 2024-02-09 |     3 |
| 2024-02-12 |     6 |
| 2024-02-13 |     8 |
| 2024-02-15 |     5 |
| 2024-02-16 |     4 |
| 2024-02-19 |     7 |
| 2024-02-20 |     9 |
| 2024-02-22 |     6 |
| 2024-02-23 |     3 |
| 2024-02-26 |     8 |`,
      { type: 'calendar-heatmap' }
    );

    dc(
      page30,
      COL2_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '106. Calendar Heatmap (no values):',
      `| Date       | Value |
| ---------- | ----- |
| 2024-01-08 |     3 |
| 2024-01-09 |     5 |
| 2024-01-10 |     8 |
| 2024-01-11 |     4 |
| 2024-01-13 |     6 |
| 2024-01-15 |     7 |
| 2024-01-16 |     3 |
| 2024-01-18 |     9 |
| 2024-01-19 |     2 |
| 2024-01-22 |     8 |
| 2024-01-24 |     5 |
| 2024-01-25 |     7 |
| 2024-01-26 |     4 |
| 2024-01-29 |     6 |
| 2024-01-30 |    10 |
| 2024-02-01 |     4 |
| 2024-02-02 |     8 |
| 2024-02-05 |     5 |
| 2024-02-06 |     7 |
| 2024-02-07 |     9 |
| 2024-02-09 |     3 |
| 2024-02-12 |     6 |
| 2024-02-13 |     8 |
| 2024-02-15 |     5 |
| 2024-02-16 |     4 |
| 2024-02-19 |     7 |
| 2024-02-20 |     9 |
| 2024-02-22 |     6 |
| 2024-02-23 |     3 |
| 2024-02-26 |     8 |`,
      { type: 'calendar-heatmap', values: 'false' }
    );

    dc(
      page30,
      COL1_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '107. Chord (trade flows):',
      `|   | A | B | C | D |
| - | - | - | - | - |
| A | 0 | 5 | 3 | 2 |
| B | 5 | 0 | 4 | 1 |
| C | 3 | 4 | 0 | 6 |
| D | 2 | 1 | 6 | 0 |`,
      { type: 'chord' }
    );

    dc(
      page30,
      COL2_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '108. Chord (no labels):',
      `|   | A | B | C | D |
| - | - | - | - | - |
| A | 0 | 5 | 3 | 2 |
| B | 5 | 0 | 4 | 1 |
| C | 3 | 4 | 0 | 6 |
| D | 2 | 1 | 6 | 0 |`,
      { type: 'chord', values: 'false' }
    );

    // ─── Page 31: Spiral & Pictogram ──────────────────────────────────────────
    const page31 = pdf.addPage();
    page31.drawString(
      'Chart Module Tests - Spiral & Pictogram',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    dc(
      page31,
      COL1_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '109. Spiral (monthly sales):',
      `| Month | Sales |
| ----- | ----- |
| Jan   |    42 |
| Feb   |    38 |
| Mar   |    55 |
| Apr   |    61 |
| May   |    70 |
| Jun   |    85 |
| Jul   |    78 |
| Aug   |    65 |
| Sep   |    72 |
| Oct   |    58 |
| Nov   |    48 |
| Dec   |    90 |`,
      { type: 'spiral' }
    );

    dc(
      page31,
      COL2_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '110. Spiral (no labels):',
      `| Month | Sales |
| ----- | ----- |
| Jan   |    42 |
| Feb   |    38 |
| Mar   |    55 |
| Apr   |    61 |
| May   |    70 |
| Jun   |    85 |
| Jul   |    78 |
| Aug   |    65 |
| Sep   |    72 |
| Oct   |    58 |
| Nov   |    48 |
| Dec   |    90 |`,
      { type: 'spiral', values: 'false' }
    );

    dc(
      page31,
      COL1_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '111. Pictogram (class sizes):',
      `| Subject | Students |
| ------- | -------- |
| Math    |       35 |
| Science |       28 |
| English |       42 |
| History |       20 |
| Art     |       15 |`,
      { type: 'pictogram' }
    );

    dc(
      page31,
      COL2_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '112. Pictogram (no values):',
      `| Subject | Students |
| ------- | -------- |
| Math    |       35 |
| Science |       28 |
| English |       42 |
| History |       20 |
| Art     |       15 |`,
      { type: 'pictogram', values: 'false' }
    );

    // ─── Page 32: Word Cloud ──────────────────────────────────────────────────
    const page32 = pdf.addPage();
    page32.drawString(
      'Chart Module Tests - Word Cloud',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    dc(
      page32,
      COL1_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '113. Word Cloud (education):',
      `| Word       | Weight |
| ---------- | ------ |
| Education  |     95 |
| Learning   |     80 |
| School     |     70 |
| Students   |     65 |
| Teachers   |     60 |
| Classroom  |     55 |
| Knowledge  |     50 |
| Science    |     48 |
| Math       |     45 |
| Reading    |     40 |
| Writing    |     38 |
| Creative   |     35 |
| Homework   |     30 |
| Study      |     28 |
| Library    |     25 |`,
      { type: 'word-cloud' }
    );

    dc(
      page32,
      COL2_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '114. Word Cloud (tech):',
      `| Word       | Weight |
| ---------- | ------ |
| Algorithm  |     90 |
| Data       |     85 |
| Code       |     80 |
| Function   |     72 |
| Variable   |     65 |
| Loop       |     58 |
| Array      |     55 |
| Object     |     50 |
| Method     |     45 |
| Class      |     40 |
| Interface  |     35 |
| Module     |     30 |
| Debug      |     25 |
| Test       |     22 |
| Deploy     |     18 |`,
      { type: 'word-cloud' }
    );

    // ─── Page 33: Gauge & Candlestick ─────────────────────────────────────────
    const page33 = pdf.addPage();
    page33.drawString(
      'Chart Module Tests - Gauge & Candlestick',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    dc(
      page33,
      COL1_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '115. Gauge (single value):',
      `| Metric | Value |
| ------ | ----- |
| Speed  |    72 |`,
      { type: 'gauge', max: '100', min: '0' }
    );

    dc(
      page33,
      COL2_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '116. Gauge (multi - 4 metrics):',
      `| Metric      | Value |
| ----------- | ----- |
| CPU         |    65 |
| Memory      |    82 |
| Disk        |    40 |
| Network     |    55 |`,
      { type: 'gauge', max: '100', min: '0' }
    );

    dc(
      page33,
      COL1_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '117. Candlestick (5 days):',
      `| Date  | Open  | High  | Low   | Close |
| ----- | ----- | ----- | ----- | ----- |
| Mon   | 100   | 115   |  95   | 110   |
| Tue   | 110   | 118   | 105   | 108   |
| Wed   | 108   | 112   |  98   | 102   |
| Thu   | 102   | 120   | 100   | 118   |
| Fri   | 118   | 125   | 112   | 115   |`,
      { type: 'candlestick' }
    );

    dc(
      page33,
      COL2_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '118. Candlestick (volatile):',
      `| Date  | Open  | High  | Low   | Close |
| ----- | ----- | ----- | ----- | ----- |
| Jan   |  50   |  80   |  40   |  70   |
| Feb   |  70   |  75   |  45   |  48   |
| Mar   |  48   |  90   |  44   |  85   |
| Apr   |  85   |  88   |  60   |  62   |
| May   |  62   | 100   |  58   |  95   |`,
      { type: 'candlestick' }
    );

    // ─── Page 34: Spline & Smooth Area ────────────────────────────────────────
    const page34 = pdf.addPage();
    page34.drawString(
      'Chart Module Tests - Spline & Smooth Area',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    dc(
      page34,
      COL1_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '119. Spline (monthly temp):',
      `| Month | Temp |
| ----- | ---- |
| Jan   |   2  |
| Feb   |   4  |
| Mar   |  10  |
| Apr   |  15  |
| May   |  20  |
| Jun   |  25  |
| Jul   |  28  |
| Aug   |  27  |
| Sep   |  22  |
| Oct   |  15  |
| Nov   |   8  |
| Dec   |   3  |`,
      { type: 'spline' }
    );

    dc(
      page34,
      COL2_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '120. Spline (website traffic):',
      `| Week | Visits |
| ---- | ------ |
| W1   |   120  |
| W2   |   145  |
| W3   |   132  |
| W4   |   180  |
| W5   |   175  |
| W6   |   220  |`,
      { type: 'spline' }
    );

    dc(
      page34,
      COL1_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '121. Smooth Area (revenue):',
      `| Quarter | Revenue |
| ------- | ------- |
| Q1 2022 |    320  |
| Q2 2022 |    410  |
| Q3 2022 |    380  |
| Q4 2022 |    490  |
| Q1 2023 |    520  |
| Q2 2023 |    610  |`,
      { type: 'smooth-area' }
    );

    dc(
      page34,
      COL2_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '122. Smooth Area (sensor data):',
      `| Time | Reading |
| ---- | ------- |
| 0s   |    10   |
| 5s   |    35   |
| 10s  |    28   |
| 15s  |    60   |
| 20s  |    45   |
| 25s  |    72   |
| 30s  |    50   |`,
      { type: 'smooth-area' }
    );

    // ─── Page 35: Number Line & Fraction Bar ──────────────────────────────────
    const page35 = pdf.addPage();
    page35.drawString(
      'Chart Module Tests - Number Line & Fraction Bar',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    dc(
      page35,
      COL1_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '123. Number Line (simple):',
      `| Label | Value |
| ----- | ----- |
| A     |     2 |
| B     |     5 |
| C     |     8 |`,
      { type: 'number-line', min: '0', max: '10', step: '1' }
    );

    dc(
      page35,
      COL2_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '124. Number Line (decimals):',
      `| Label | Value |
| ----- | ----- |
| Min   |   1.5 |
| Med   |   3.7 |
| Max   |   4.8 |`,
      { type: 'number-line', min: '0', max: '6', step: '1' }
    );

    dc(
      page35,
      COL1_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '125. Fraction Bar (simple fractions):',
      `| Label | Num | Den |
| ----- | --- | --- |
| 1/2   |   1 |   2 |
| 3/4   |   3 |   4 |
| 2/3   |   2 |   3 |
| 5/8   |   5 |   8 |
| 1/4   |   1 |   4 |`,
      { type: 'fraction-bar' }
    );

    dc(
      page35,
      COL2_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '126. Fraction Bar (larger denominators):',
      `| Label   | Num | Den |
| ------- | --- | --- |
| 3/5     |   3 |   5 |
| 7/10    |   7 |  10 |
| 4/6     |   4 |   6 |
| 9/12    |   9 |  12 |`,
      { type: 'fraction-bar' }
    );

    // ─── Page 36: Pie Grid ────────────────────────────────────────────────────
    const page36 = pdf.addPage();
    page36.drawString(
      'Chart Module Tests - Pie Grid',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    dc(
      page36,
      COL1_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '127. Pie Grid (3 countries):',
      `| Country | Cars | Bikes | Buses |
| ------- | ---- | ----- | ----- |
| CZ      |   60 |    25 |    15 |
| SK      |   55 |    30 |    15 |
| AT      |   70 |    15 |    15 |`,
      { type: 'pie-grid' }
    );

    dc(
      page36,
      COL2_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '128. Pie Grid (budget split):',
      `| Dept    | Staff | Equipment | Other |
| ------- | ----- | --------- | ----- |
| IT      |    50 |        35 |    15 |
| HR      |    75 |         5 |    20 |
| Sales   |    60 |        15 |    25 |
| Finance |    65 |        10 |    25 |`,
      { type: 'pie-grid' }
    );

    dc(
      page36,
      COL1_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '129. Pie Grid (2 pies):',
      `| Group | A  | B  | C  |
| ----- | -- | -- | -- |
| 2022  | 40 | 35 | 25 |
| 2023  | 30 | 45 | 25 |`,
      { type: 'pie-grid' }
    );

    dc(
      page36,
      COL2_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '130. Pie Grid (single pie):',
      `| Label | Dogs | Cats | Fish | Birds |
| ----- | ---- | ---- | ---- | ----- |
| Pets  |   45 |   30 |   15 |    10 |`,
      { type: 'pie-grid' }
    );

    // ─── Page 37: Violin Plot + Dot Plot ────────────────────────────────────
    const page37 = pdf.addPage();
    page37.drawString(
      'Chart Module Tests - Violin Plot & Dot Plot',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    dc(
      page37,
      COL1_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '131. Violin Plot (3 groups):',
      `| Group | Min | Q1 | Median | Q3 | Max |
| ----- | --- | -- | ------ | -- | --- |
| A     |  10 | 20 |     30 | 45 |  60 |
| B     |  15 | 25 |     40 | 55 |  70 |
| C     |   5 | 15 |     25 | 35 |  50 |`,
      { type: 'violin-plot' }
    );

    dc(
      page37,
      COL2_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '132. Violin Plot (scores):',
      `| Subject | Min | Q1 | Median | Q3 | Max |
| ------- | --- | -- | ------ | -- | --- |
| Math    |  40 | 55 |     70 | 82 |  95 |
| Reading |  50 | 65 |     75 | 85 |  98 |
| Science |  35 | 52 |     68 | 80 |  92 |
| Art     |  60 | 72 |     80 | 90 | 100 |`,
      { type: 'violin-plot' }
    );

    dc(
      page37,
      COL1_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '133. Dot Plot (temperatures):',
      `| City      | Temp |
| --------- | ---- |
| London    |   14 |
| Paris     |   16 |
| Madrid    |   22 |
| Rome      |   24 |
| Athens    |   28 |
| Berlin    |   13 |`,
      { type: 'dot-plot' }
    );

    dc(
      page37,
      COL2_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '134. Dot Plot (test scores):',
      `| Student | Score |
| ------- | ----- |
| Alice   |    88 |
| Bob     |    74 |
| Carol   |    92 |
| David   |    65 |
| Eve     |    81 |`,
      { type: 'dot-plot' }
    );

    // ─── Page 38: Ten Frame + Tally Chart ────────────────────────────────────
    const page38 = pdf.addPage();
    page38.drawString(
      'Chart Module Tests - Ten Frame & Tally Chart',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    dc(
      page38,
      COL1_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '135. Ten Frame (3 frames):',
      `| Item    | Count |
| ------- | ----- |
| Apples  |     7 |
| Oranges |     4 |
| Bananas |    10 |`,
      { type: 'ten-frame' }
    );

    dc(
      page38,
      COL2_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '136. Ten Frame (single):',
      `| Label | Count |
| ----- | ----- |
| Stars |     6 |`,
      { type: 'ten-frame' }
    );

    dc(
      page38,
      COL1_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '137. Tally Chart (votes):',
      `| Option    | Count |
| --------- | ----- |
| Red       |     7 |
| Blue      |    13 |
| Green     |     5 |
| Yellow    |     9 |`,
      { type: 'tally-chart' }
    );

    dc(
      page38,
      COL2_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '138. Tally Chart (survey):',
      `| Answer  | Count |
| ------- | ----- |
| Always  |    12 |
| Often   |     8 |
| Sometimes |   6 |
| Never   |     3 |`,
      { type: 'tally-chart' }
    );

    // ─── Page 39: Place Value + Fraction Circles ──────────────────────────────
    const page39 = pdf.addPage();
    page39.drawString(
      'Chart Module Tests - Place Value & Fraction Circles',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    dc(
      page39,
      COL1_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '139. Place Value (3-digit):',
      `| Number | Value |
| ------ | ----- |
| First  |   234 |
| Second |   507 |
| Third  |   819 |
| Fourth |   100 |`,
      { type: 'place-value' }
    );

    dc(
      page39,
      COL2_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '140. Place Value (4-digit):',
      `| Number | Value |
| ------ | ----- |
| Alfa   |  1234 |
| Beta   |  2050 |
| Gamma  |  3007 |`,
      { type: 'place-value' }
    );

    dc(
      page39,
      COL1_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '141. Fraction Circles (halves/thirds/quarters):',
      `| Fraction | Value |
| -------- | ----- |
| 1/2      |     0 |
| 2/3      |     0 |
| 3/4      |     0 |
| 1/4      |     0 |
| 3/3      |     0 |
| 0/2      |     0 |`,
      { type: 'fraction-circles' }
    );

    dc(
      page39,
      COL2_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '142. Fraction Circles (fifths/sixths):',
      `| Fraction | Value |
| -------- | ----- |
| 2/5      |     0 |
| 4/6      |     0 |
| 3/5      |     0 |
| 5/6      |     0 |`,
      { type: 'fraction-circles' }
    );

    // ─── Page 40: Multiplication Grid ────────────────────────────────────────
    const page40 = pdf.addPage();
    page40.drawString(
      'Chart Module Tests - Multiplication Grid',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    dc(
      page40,
      COL1_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '143. Multiplication Grid (1-10, x*y):',
      `| Label | Value |
| ----- | ----- |`,
      { type: 'multiplication-grid', from: '1', to: '10', topLeft: '×' }
    );

    dc(
      page40,
      COL2_X,
      ROW1_Y,
      CHART_W,
      CHART_H,
      '144. Multiplication Grid (1-6, x+y):',
      `| Label | Value |
| ----- | ----- |`,
      { type: 'multiplication-grid', from: '1', to: '6', fn: 'x + y', topLeft: '+' }
    );

    dc(
      page40,
      COL1_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '145. Multiplication Grid (asymmetric x:1-5, y:1-10):',
      `| Label | Value |
| ----- | ----- |`,
      { type: 'multiplication-grid', xMin: '1', xMax: '5', yMin: '1', yMax: '10', topLeft: '×' }
    );

    dc(
      page40,
      COL2_X,
      ROW2_Y,
      CHART_W,
      CHART_H,
      '146. Multiplication Grid (squares, 1-8, x²):',
      `| Label | Value |
| ----- | ----- |`,
      {
        type: 'multiplication-grid',
        xMin: '1',
        xMax: '8',
        yMin: '1',
        yMax: '8',
        fn: 'x * x',
        topLeft: 'n²',
      }
    );
  }
}
